import { Controller } from '@hotwired/stimulus'
import type SelectController from '../../components/Select/controller'
import type InputController from '../../components/Input/controller'

interface BankSuggestion {
    name: string
    bik: string
}

export default class extends Controller {
    static targets = ['inn', 'name', 'kpp', 'address', 'paymentBik', 'paymentBankName']
    static values = {
        lookupUrl: { type: String, default: '/api/buyer/companies/lookup' },
        bankSuggestUrl: { type: String, default: '/api/buyer/companies/bank-suggest' },
        bankFindUrl: { type: String, default: '/api/buyer/companies/bank-find' },
    }

    declare readonly innTarget: HTMLInputElement
    declare readonly nameTarget: HTMLInputElement
    declare readonly kppTarget: HTMLInputElement
    declare readonly addressTarget: HTMLInputElement
    declare readonly paymentBikTarget: HTMLInputElement
    declare readonly paymentBankNameTarget: HTMLInputElement

    declare readonly lookupUrlValue: string
    declare readonly bankSuggestUrlValue: string
    declare readonly bankFindUrlValue: string

    private inputTimer: number | null = null
    private bankSuggestTimer: number | null = null
    private bikTimer: number | null = null
    private bankSuggestions: BankSuggestion[] = []
    private bankSuggestAbortController: AbortController | null = null

    connect(): void {
        const valueInput = this.paymentBankNameTarget
            .closest('.with-dropdown')
            ?.querySelector<HTMLInputElement>('[data-select-target="value"]')

        valueInput?.addEventListener('change', this.onBankSelected)

        const inn = this.innTarget.value.trim()

        if (inn.length >= 10) {
            this.fetchCompany(inn)
        }

        const bik = this.paymentBikTarget.value.trim()

        if (bik.length === 9) {
            this.fetchBankByBik(bik)
        }

        const bankName = (this.paymentBankNameTarget.value ?? '').trim()

        if (bankName.length > 0) {
            this.fetchBankSuggest()
        }
    }

    disconnect(): void {
        const valueInput = this.paymentBankNameTarget
            .closest('.with-dropdown')
            ?.querySelector<HTMLInputElement>('[data-select-target="value"]')

        valueInput?.removeEventListener('change', this.onBankSelected)
    }

    private onBankSelected = (): void => {
        const selected = this.paymentBankNameTarget.value

        if (!selected) {
            return
        }

        const bank = this.bankSuggestions.find(b => b.name === selected || b.bik === selected)

        if (bank) {
            this.resolveBikController()?.setValue(bank.bik)
        }
    }

    innChanged(): void {
        if (this.inputTimer) {
            clearTimeout(this.inputTimer)
        }

        const raw = this.resolveInnController()?.getValue() ?? this.innTarget.value
        const inn = (raw.match(/\d+/g) || []).join('')

        if (inn.length !== 10 && inn.length !== 12) {
            this.nameTarget.value = ''
            this.kppTarget.value = ''
            this.addressTarget.value = ''
            return
        }

        this.inputTimer = window.setTimeout(() => this.fetchCompany(inn), 500)
    }

    async fetchCompany(inn: string): Promise<void> {
        const innController = this.resolveInnController()
        innController?.clearError()

        try {
            const response = await fetch(
                `${this.lookupUrlValue}?inn=${encodeURIComponent(inn)}`,
                { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
            )

            if (!response.ok) {
                const errorData = await response.json().catch(() => null)
                const message = errorData?.error ?? 'Не найдена организация с указанным ИНН'
                innController?.setError(message)

                return
            }

            const data = await response.json()

            this.setFieldValue(this.nameTarget, data.name)
            this.setFieldValue(this.kppTarget, data.kpp)
            this.setFieldValue(this.addressTarget, data.address)
        } catch {
            innController?.setError('Не удалось получить данные о компании')
        }
    }

    bankNameInput(): void {
        if (this.bankSuggestTimer) {
            clearTimeout(this.bankSuggestTimer)
        }

        this.bankSuggestTimer = window.setTimeout(() => this.fetchBankSuggest(), 500)
    }

    async fetchBankSuggest(): Promise<void> {
        if (this.bankSuggestAbortController) {
            this.bankSuggestAbortController.abort()
        }

        this.bankSuggestAbortController = new AbortController()

        const query = (this.paymentBankNameTarget.value || '').trim()

        if (query.length < 2) {
            return
        }

        try {
            const response = await fetch(
                `${this.bankSuggestUrlValue}?query=${encodeURIComponent(query)}`,
                {
                    headers: { 'X-Requested-With': 'XMLHttpRequest' },
                    signal: this.bankSuggestAbortController.signal,
                }
            )

            if (!response.ok) {
                return
            }

            const banks: BankSuggestion[] = await response.json()

            if (!Array.isArray(banks) || banks.length === 0) {
                return
            }

            this.bankSuggestions = banks
            this.resolveSelectController()?.replaceOptions(banks.map(b => ({ value: b.name, label: b.name })))
        } catch {
            // ignore
        }
    }

    bikChanged(): void {
        if (this.bikTimer) {
            clearTimeout(this.bikTimer)
        }

        const raw = this.resolveBikController()?.getValue() ?? this.paymentBikTarget.value
        const bik = (raw.match(/\d+/g) || []).join('')

        if (bik.length !== 9) {
            return
        }

        this.bikTimer = window.setTimeout(() => this.fetchBankByBik(bik), 500)
    }

    async fetchBankByBik(bik: string): Promise<void> {
        try {
            const response = await fetch(
                `${this.bankFindUrlValue}?bik=${encodeURIComponent(bik)}`,
                { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
            )

            if (!response.ok) {
                return
            }

            const bank = await response.json()

            if (bank && bank.name) {
                this.bankSuggestions = [{ name: bank.name, bik: bank.bik }]
                this.resolveSelectController()?.replaceOptions([{ value: bank.name, label: bank.name }])

                const wrapper = this.paymentBankNameTarget.closest('.with-dropdown')

                if (wrapper) {
                    const searchInput = wrapper.querySelector<HTMLInputElement>('[data-select-target="input"]')
                    const valueInput = wrapper.querySelector<HTMLInputElement>('[data-select-target="value"]')

                    if (searchInput) {
                        searchInput.value = bank.name
                    }

                    if (valueInput) {
                        valueInput.value = bank.name
                    }
                }
            }
        } catch {
            // ignore
        }
    }

    private setFieldValue(element: HTMLInputElement, value: string | null | undefined): void {
        if (value == null) {
            return
        }

        const controller = this.resolveInputController(element)
        controller ? controller.setValue(value) : (element.value = value)
    }

    private resolveSelectController(): SelectController | null {
        const element = this.element.querySelector<HTMLElement>('[data-controller~="select"]')

        return element
            ? this.application.getControllerForElementAndIdentifier(element, 'select') as SelectController | null
            : null
    }

    private resolveInputController(element: HTMLElement): InputController | null {
        const wrapper = element.closest<HTMLElement>('[data-controller~="input"]')

        return wrapper
            ? this.application.getControllerForElementAndIdentifier(wrapper, 'input') as InputController | null
            : null
    }

    private resolveInnController(): InputController | null {
        return this.resolveInputController(this.innTarget)
    }

    private resolveBikController(): InputController | null {
        return this.resolveInputController(this.paymentBikTarget)
    }
}
